import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaPause,
  FaPlay,
  FaStepBackward,
  FaStepForward,
} from "react-icons/fa";
import TvPreviewFrame from "../../components/tv-disply/TvPreviewFrame";
import TvEmptyState from "../../components/tv-disply/TvEmptyState";
import TvPresentationPreviewCanvas from "../../components/tv-disply/TvPresentationPreviewCanvas";
import {
  DEFAULT_IMAGE_DURATION_SECONDS,
  TV_MEDIA_TYPES,
} from "../../services/tv-disply/tvDisplayConstants";
import { subscribeToTvPlaylistItems } from "../../services/tv-disply/tvPlaylistService";

export default function TvPreview({ config, playlistRows = [], mediaRows = [] }) {
  const [selectedPlaylistId, setSelectedPlaylistId] = useState("");
  const [playlistItems, setPlaylistItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState("");
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (selectedPlaylistId && playlistRows.some((playlist) => playlist.id === selectedPlaylistId)) return;
    setSelectedPlaylistId(playlistRows[0]?.id || "");
  }, [playlistRows, selectedPlaylistId]);

  useEffect(() => {
    setPreviewIndex(0);
    setPreviewPlaying(false);
  }, [selectedPlaylistId]);

  useEffect(() => {
    if (!selectedPlaylistId) {
      setPlaylistItems([]);
      setItemsLoading(false);
      setItemsError("");
      return undefined;
    }

    setItemsLoading(true);
    const unsubscribe = subscribeToTvPlaylistItems(
      selectedPlaylistId,
      (rows) => {
        setPlaylistItems(rows);
        setItemsLoading(false);
        setItemsError("");
      },
      (error) => {
        setItemsLoading(false);
        setItemsError(error?.message || "Unable to load playlist preview items.");
      }
    );

    return () => unsubscribe();
  }, [selectedPlaylistId]);

  const selectedPlaylist = useMemo(
    () => playlistRows.find((playlist) => playlist.id === selectedPlaylistId) || null,
    [playlistRows, selectedPlaylistId]
  );

  const mediaMap = useMemo(
    () => new Map(mediaRows.map((media) => [media.id, media])),
    [mediaRows]
  );

  const previewEntries = useMemo(
    () =>
      playlistItems.map((item) => {
        const media = mediaMap.get(item.mediaId) || null;
        const missingMedia = !media;
        const disabledMedia = Boolean(media && media.enabled === false);
        return {
          ...item,
          media,
          missingMedia,
          disabledMedia,
        };
      }),
    [mediaMap, playlistItems]
  );

  const previewableItems = useMemo(
    () => previewEntries.filter((entry) => entry.media?.downloadURL),
    [previewEntries]
  );

  const currentItem = previewableItems[previewIndex] || null;
  const displayPlaylist = selectedPlaylist || { presentation: config };

  useEffect(() => {
    if (!previewableItems.length) {
      setPreviewIndex(0);
      setPreviewPlaying(false);
      return;
    }

    if (previewIndex < previewableItems.length) return;
    setPreviewIndex(0);
  }, [previewIndex, previewableItems.length]);

  useEffect(() => {
    if (!previewPlaying || !currentItem || currentItem.media?.type !== TV_MEDIA_TYPES.IMAGE) return undefined;

    const durationSeconds =
      currentItem.imageDurationSeconds ||
      selectedPlaylist?.presentation?.defaultImageDurationSeconds ||
      DEFAULT_IMAGE_DURATION_SECONDS;

    const timer = window.setTimeout(() => {
      setPreviewIndex((current) => nextIndex(current, previewableItems.length));
    }, Math.max(1, durationSeconds) * 1000);

    return () => window.clearTimeout(timer);
  }, [
    currentItem,
    previewPlaying,
    previewableItems.length,
    selectedPlaylist?.presentation?.defaultImageDurationSeconds,
  ]);

  function handleTogglePlayback() {
    if (!previewableItems.length) return;
    setPreviewPlaying((current) => !current);
  }

  function handlePrevious() {
    if (!previewableItems.length) return;
    setPreviewPlaying(false);
    setPreviewIndex((current) => previousIndex(current, previewableItems.length));
  }

  function handleNext() {
    if (!previewableItems.length) return;
    setPreviewPlaying(false);
    setPreviewIndex((current) => nextIndex(current, previewableItems.length));
  }

  return (
    <section className="tv-display-panel">
      <div className="tv-display-panel-head">
        <div>
          <h2 className="tv-display-panel-title">Live Preview</h2>
          <p className="tv-display-panel-copy">
            Preview a real draft playlist using its actual playlist items, Media Library records,
            presentation settings, and per-item overlay content.
          </p>
        </div>
      </div>

      <div className="tv-display-preview-controls">
        <label className="tv-display-field">
          <span className="tv-display-field-label">Select Playlist</span>
          <select
            className="tv-media-select"
            value={selectedPlaylistId}
            onChange={(event) => setSelectedPlaylistId(event.target.value)}
          >
            <option value="">Select a playlist to preview.</option>
            {playlistRows.map((playlist) => (
              <option key={playlist.id} value={playlist.id}>
                {playlist.name} · {playlist.enabled ? "Enabled" : "Disabled"} · {playlist.itemCount} items · {playlist.presentation?.displayMode || "MIXED_PLAYLIST"} · {playlist.presentation?.orientation || "LANDSCAPE"}
              </option>
            ))}
          </select>
        </label>

        {selectedPlaylist ? (
          <div className="tv-display-preview-summary">
            <span className="tv-display-chip is-soft">{selectedPlaylist.enabled ? "Enabled" : "Disabled"}</span>
            <span className="tv-display-chip is-soft">{selectedPlaylist.presentation?.displayMode || "MIXED_PLAYLIST"}</span>
            <span className="tv-display-chip is-soft">{selectedPlaylist.presentation?.orientation || "LANDSCAPE"}</span>
            <span className="tv-display-chip is-soft">{selectedPlaylist.itemCount} items</span>
          </div>
        ) : null}
      </div>

      {itemsError ? <div className="tv-display-feedback is-error">{itemsError}</div> : null}

      <TvPreviewFrame title="TV Display Preview">
        {!selectedPlaylistId ? (
          <TvEmptyState
            title="Select a playlist to preview."
            copy="Choose any saved playlist to load its real playlist items and presentation settings."
          />
        ) : itemsLoading ? (
          <div className="tv-display-feedback is-loading">Loading playlist items for preview.</div>
        ) : !previewEntries.length ? (
          <TvEmptyState
            title="This playlist has no media to preview."
            copy="Add Media Library items to this playlist first, then return here to review its presentation."
          />
        ) : !previewableItems.length ? (
          <TvEmptyState
            title="Media unavailable"
            copy="All playlist items are currently missing their linked Media Library records."
          />
        ) : (
          <>
            <div className="tv-display-preview-toolbar">
              <div className="tv-display-preview-toolbar-copy">
                <div className="tv-display-preview-mode">{selectedPlaylist?.name || "Playlist Preview"}</div>
                <div className="tv-display-preview-meta">
                  {previewIndex + 1} / {previewableItems.length} · {currentItem?.media?.type || "MEDIA"} · {selectedPlaylist?.presentation?.fitMode || "CROP"} · {selectedPlaylist?.presentation?.transition || "FADE"}
                </div>
              </div>

              <div className="tv-display-card-actions is-wrap">
                <button type="button" className="tv-display-action-btn" onClick={handlePrevious}>
                  <FaStepBackward />
                  Previous
                </button>
                <button type="button" className="tv-display-action-btn is-primary" onClick={handleTogglePlayback}>
                  {previewPlaying ? <FaPause /> : <FaPlay />}
                  {previewPlaying ? "Pause" : "Play"}
                </button>
                <button type="button" className="tv-display-action-btn" onClick={handleNext}>
                  <FaStepForward />
                  Next
                </button>
              </div>
            </div>

            <TvPresentationPreviewCanvas
              key={`${selectedPlaylistId}_${currentItem.id}_${previewIndex}`}
              playlist={displayPlaylist}
              item={currentItem}
              muted
              autoPlayVideo={previewPlaying}
              playing={previewPlaying}
              videoRef={videoRef}
              onVideoEnded={() => setPreviewIndex((current) => nextIndex(current, previewableItems.length))}
            />

            <PreviewWarnings entries={previewEntries} />
          </>
        )}
      </TvPreviewFrame>
    </section>
  );
}

function PreviewWarnings({ entries }) {
  const warnings = entries.flatMap((entry) => {
    if (entry.missingMedia) {
      return [`Item ${entry.order + 1}: Media unavailable`];
    }
    if (entry.disabledMedia) {
      return [`Item ${entry.order + 1}: Linked media is disabled in the Media Library`];
    }
    return [];
  });

  if (!warnings.length) {
    return (
      <div className="tv-display-feedback is-success">
        <FaCheckCircle /> Preview is using real playlist items and linked Media Library records.
      </div>
    );
  }

  return (
    <div className="tv-display-warning-list">
      {warnings.map((warning) => (
        <div key={warning} className="tv-display-warning-line">
          <FaExclamationTriangle />
          <span>{warning}</span>
        </div>
      ))}
    </div>
  );
}

function nextIndex(current, total) {
  if (total <= 1) return 0;
  return (current + 1) % total;
}

function previousIndex(current, total) {
  if (total <= 1) return 0;
  return current === 0 ? total - 1 : current - 1;
}
